import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { PALETTE } from './stationTheme';

export type TimeRange = '1D' | '7D' | '1M' | 'YTD';

interface PriceDataPoint {
  time: number; // Unix timestamp in ms
  price: number;
  volume: number;
}

interface PriceHistoryChartProps {
  data: PriceDataPoint[];
  itemName: string;
  height?: number;
}

// Calculate Bollinger Bands (20-period SMA with 2 standard deviations)
function calculateBollingerBands(
  prices: number[],
  period: number = 20
): { upper: number[]; middle: number[]; lower: number[] } {
  const upper: number[] = [];
  const middle: number[] = [];
  const lower: number[] = [];

  // Fill with NaN for initial points where we don't have enough data
  for (let i = 0; i < period - 1 && i < prices.length; i++) {
    upper.push(NaN);
    middle.push(NaN);
    lower.push(NaN);
  }

  for (let i = period - 1; i < prices.length; i++) {
    const slice = prices.slice(i - period + 1, i + 1);
    const sma = slice.reduce((a, b) => a + b, 0) / period;
    const variance = slice.reduce((sum, price) => sum + Math.pow(price - sma, 2), 0) / period;
    const stdDev = Math.sqrt(variance);

    middle.push(sma);
    upper.push(sma + 2 * stdDev);
    lower.push(sma - 2 * stdDev);
  }

  return { upper, middle, lower };
}

export function PriceHistoryChart({ data, itemName, height = 280 }: PriceHistoryChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('7D');
  const [tooltip, setTooltip] = useState<{ x: number; y: number; data: PriceDataPoint } | null>(null);

  // Filter data by time range
  const filteredData = useMemo(() => {
    if (data.length === 0) return [];

    const now = Date.now();
    let cutoff: number;

    switch (timeRange) {
      case '1D':
        cutoff = now - 24 * 60 * 60 * 1000;
        break;
      case '7D':
        cutoff = now - 7 * 24 * 60 * 60 * 1000;
        break;
      case '1M':
        cutoff = now - 30 * 24 * 60 * 60 * 1000;
        break;
      case 'YTD':
        cutoff = new Date(new Date().getFullYear(), 0, 1).getTime();
        break;
      default:
        cutoff = now - 7 * 24 * 60 * 60 * 1000;
    }

    return data.filter(d => d.time >= cutoff).sort((a, b) => a.time - b.time);
  }, [data, timeRange]);

  // Draw chart
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const chartHeight = height;
    const padding = { top: 20, right: 60, bottom: 30, left: 10 };
    const chartWidth = width - padding.left - padding.right;
    const priceChartHeight = chartHeight * 0.75 - padding.top;
    const volumeChartHeight = chartHeight * 0.2;
    const volumeTop = chartHeight - volumeChartHeight - padding.bottom;

    // Clear
    ctx.fillStyle = PALETTE.DARK;
    ctx.fillRect(0, 0, width, chartHeight);

    if (filteredData.length === 0) {
      ctx.fillStyle = PALETTE.DIM;
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('No price history available', width / 2, chartHeight / 2);
      return;
    }

    // Calculate price range
    const prices = filteredData.map(d => d.price);
    const volumes = filteredData.map(d => d.volume);

    // Calculate Bollinger bands
    const period = Math.min(20, Math.floor(prices.length / 2)) || prices.length;
    const bands = period > 1 ? calculateBollingerBands(prices, period) : null;

    // Find min/max including bands
    let minPrice = Math.min(...prices);
    let maxPrice = Math.max(...prices);
    if (bands) {
      const validUpper = bands.upper.filter(v => !isNaN(v));
      const validLower = bands.lower.filter(v => !isNaN(v));
      if (validUpper.length > 0) maxPrice = Math.max(maxPrice, ...validUpper);
      if (validLower.length > 0) minPrice = Math.min(minPrice, ...validLower);
    }

    // Add 10% padding to price range
    const priceRange = maxPrice - minPrice || 1;
    minPrice -= priceRange * 0.1;
    maxPrice += priceRange * 0.1;

    const maxVolume = Math.max(...volumes) || 1;

    // Helper functions
    const xScale = (i: number) => padding.left + (i / (filteredData.length - 1 || 1)) * chartWidth;
    const yPriceScale = (price: number) => {
      return padding.top + priceChartHeight - ((price - minPrice) / (maxPrice - minPrice)) * priceChartHeight;
    };
    const yVolumeScale = (vol: number) => {
      return volumeTop + volumeChartHeight - (vol / maxVolume) * volumeChartHeight;
    };

    // Draw grid lines
    ctx.strokeStyle = PALETTE.BORDER;
    ctx.lineWidth = 1;

    // Horizontal grid (price)
    const priceGridCount = 4;
    for (let i = 0; i <= priceGridCount; i++) {
      const y = padding.top + (i / priceGridCount) * priceChartHeight;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();

      // Price label
      const price = maxPrice - (i / priceGridCount) * (maxPrice - minPrice);
      ctx.fillStyle = PALETTE.DIM;
      ctx.font = '10px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(Math.round(price).toLocaleString(), width - padding.right + 5, y + 4);
    }

    // Draw Bollinger bands fill
    if (bands && bands.upper.some(v => !isNaN(v))) {
      ctx.fillStyle = PALETTE.DIM + '15'; // Very transparent
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < filteredData.length; i++) {
        if (!isNaN(bands.upper[i])) {
          const x = xScale(i);
          const y = yPriceScale(bands.upper[i]);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      for (let i = filteredData.length - 1; i >= 0; i--) {
        if (!isNaN(bands.lower[i])) {
          ctx.lineTo(xScale(i), yPriceScale(bands.lower[i]));
        }
      }
      ctx.closePath();
      ctx.fill();

      // Draw band lines
      ctx.strokeStyle = PALETTE.DIM + '60';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);

      // Upper band
      ctx.beginPath();
      started = false;
      for (let i = 0; i < filteredData.length; i++) {
        if (!isNaN(bands.upper[i])) {
          const x = xScale(i);
          const y = yPriceScale(bands.upper[i]);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      ctx.stroke();

      // Lower band
      ctx.beginPath();
      started = false;
      for (let i = 0; i < filteredData.length; i++) {
        if (!isNaN(bands.lower[i])) {
          const x = xScale(i);
          const y = yPriceScale(bands.lower[i]);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      ctx.stroke();

      // SMA line (middle band)
      ctx.strokeStyle = PALETTE.ORANGE;
      ctx.lineWidth = 1;
      ctx.beginPath();
      started = false;
      for (let i = 0; i < filteredData.length; i++) {
        if (!isNaN(bands.middle[i])) {
          const x = xScale(i);
          const y = yPriceScale(bands.middle[i]);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw volume bars
    const barWidth = Math.max(2, chartWidth / filteredData.length - 1);
    ctx.fillStyle = PALETTE.TEAL + '60';
    for (let i = 0; i < filteredData.length; i++) {
      const x = xScale(i) - barWidth / 2;
      const y = yVolumeScale(filteredData[i].volume);
      const h = volumeTop + volumeChartHeight - y;
      ctx.fillRect(x, y, barWidth, h);
    }

    // Draw price line
    ctx.strokeStyle = PALETTE.TEAL;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < filteredData.length; i++) {
      const x = xScale(i);
      const y = yPriceScale(filteredData[i].price);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();

    // Draw price points
    ctx.fillStyle = PALETTE.TEAL;
    for (let i = 0; i < filteredData.length; i++) {
      const x = xScale(i);
      const y = yPriceScale(filteredData[i].price);
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw time labels
    ctx.fillStyle = PALETTE.DIM;
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';

    const labelCount = Math.min(5, filteredData.length);
    for (let i = 0; i < labelCount; i++) {
      const idx = Math.floor(i * (filteredData.length - 1) / (labelCount - 1 || 1));
      const x = xScale(idx);
      const date = new Date(filteredData[idx].time);
      let label: string;
      if (timeRange === '1D') {
        label = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else {
        label = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
      ctx.fillText(label, x, chartHeight - 8);
    }
  }, [filteredData, height, timeRange]);

  // Handle resize
  useEffect(() => {
    drawChart();

    const handleResize = () => drawChart();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawChart]);

  // Handle mouse move for tooltip
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || filteredData.length === 0) {
      setTooltip(null);
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const padding = { left: 10, right: 60 };
    const chartWidth = rect.width - padding.left - padding.right;

    const dataIndex = Math.round((x - padding.left) / chartWidth * (filteredData.length - 1));
    if (dataIndex >= 0 && dataIndex < filteredData.length) {
      setTooltip({
        x: e.clientX - container.getBoundingClientRect().left,
        y: e.clientY - container.getBoundingClientRect().top,
        data: filteredData[dataIndex],
      });
    } else {
      setTooltip(null);
    }
  }, [filteredData]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  const timeRanges: TimeRange[] = ['1D', '7D', '1M', 'YTD'];

  const formatTooltipTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={containerStyle} ref={containerRef}>
      {/* Time Range Selector */}
      <div style={timeRangeSelectorStyle}>
        {timeRanges.map(range => (
          <button
            key={range}
            style={{
              ...timeRangeButtonStyle,
              backgroundColor: timeRange === range ? PALETTE.TEAL : 'transparent',
              color: timeRange === range ? PALETTE.DARK : PALETTE.DIM,
            }}
            onClick={() => setTimeRange(range)}
          >
            {range}
          </button>
        ))}
      </div>

      {/* Chart Canvas */}
      <div style={{ position: 'relative' }}>
        <canvas
          ref={canvasRef}
          style={chartStyle}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />

        {/* Tooltip */}
        {tooltip && (
          <div style={{
            ...tooltipStyle,
            left: tooltip.x + 10,
            top: tooltip.y - 60,
          }}>
            <div style={{ color: PALETTE.LIGHT, marginBottom: '4px' }}>
              {formatTooltipTime(tooltip.data.time)}
            </div>
            <div>
              <span style={{ color: PALETTE.DIM }}>Price: </span>
              <span style={{ color: PALETTE.TEAL }}>{tooltip.data.price.toLocaleString()} CR</span>
            </div>
            <div>
              <span style={{ color: PALETTE.DIM }}>Volume: </span>
              <span style={{ color: PALETTE.TEAL }}>{tooltip.data.volume.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={legendStyle}>
        <span style={{ color: PALETTE.TEAL }}>● Price</span>
        <span style={{ color: PALETTE.ORANGE }}>● SMA({Math.min(20, Math.floor(filteredData.length / 2)) || 'N/A'})</span>
        <span style={{ color: PALETTE.DIM }}>● Bollinger Bands</span>
        <span style={{ color: PALETTE.TEAL + '60' }}>● Volume</span>
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  fontFamily: "'JetBrains Mono', monospace",
};

const timeRangeSelectorStyle: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
  padding: '8px 0',
};

const timeRangeButtonStyle: React.CSSProperties = {
  padding: '4px 12px',
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: '3px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  fontWeight: 600,
  transition: 'all 0.15s',
};

const chartStyle: React.CSSProperties = {
  width: '100%',
  cursor: 'crosshair',
};

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: '4px',
  padding: '8px 12px',
  fontSize: '11px',
  pointerEvents: 'none',
  zIndex: 10,
  whiteSpace: 'nowrap',
};

const legendStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  padding: '8px 0',
  fontSize: '10px',
};

export default PriceHistoryChart;
